import Api from "../../../API/API.js";


const isStimOn = (stim) => {
  // stim: [width_us, duration_ms, amp_uA, hold_s]
  const w = Number(stim?.[0]) || 0;
  const a = Number(stim?.[2]) || 0;
  // 자극 여부 기준: width>0 AND amp>0 (프로젝트에서 0/0이면 휴식 의미가 많음)
  return w > 0 && a > 0;
};

const sumHoldsByTypeInOrder = (stims) => {
  // 연속된 같은 타입(자극/휴식)끼리 hold를 합쳐서 [{type, sec}, ...]로 반환
  const segs = [];
  for (const s of stims) {
    const sec = Math.max(0, Number(s?.[3]) || 0);
    const type = isStimOn(s) ? "stim" : "rest";
    if (segs.length === 0 || segs[segs.length - 1].type !== type) {
      segs.push({ type, sec });
    } else {
      segs[segs.length - 1].sec += sec;
    }
  }
  return segs;
};

const formatSec = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return r ? `${h}시간 ${mm}분 ${r}초` : `${h}시간 ${mm}분`;
  }
  return m ? (r ? `${m}분 ${r}초` : `${m}분`) : `${r}초`;
};

export const describeRecipeBlocksHuman = (recipeJson) => {
  if (!recipeJson || !Array.isArray(recipeJson.blocks)) return [];

  return recipeJson.blocks.map((block, idx) => {
    const repeat = Number(block?.repeat) || 1;
    const stims = Array.isArray(block?.stims) ? block.stims : [];

    const segs = sumHoldsByTypeInOrder(stims); // 1회 루프 기준
    const loopSec = segs.reduce((acc, x) => acc + x.sec, 0);
    const totalSec = loopSec * repeat;

    // 1) 가장 원하는 케이스: (자극 -> 휴식) 2개 세그먼트로 끝나면
    //    "n회 반복, 자극 X초 휴식 Y초" 형태로.
    const isSimpleStimRest =
      segs.length === 2 && segs[0].type === "stim" && segs[1].type === "rest";

    // 2) 그 외는 "자극 A → 휴식 B → 자극 C ..." 형태로 나열
    const segText = isSimpleStimRest
      ? `자극 ${formatSec(segs[0].sec)} · 휴식 ${formatSec(segs[1].sec)}`
      : segs
          .filter((x) => x.sec > 0)
          .map((x) => (x.type === "stim" ? `자극 ${formatSec(x.sec)}` : `휴식 ${formatSec(x.sec)}`))
          .join(" → ");

    // repeat 표현: repeat>1일 때만 "n회 반복, ..." 붙이고, 아니면 그냥 블록 합계만.
    const repeatPrefix = repeat > 1 ? `${repeat}회 반복, ` : "";

    // 블록 라인: 블록1: 7회 반복, 자극 30초 · 휴식 15초 (총 3분 15초)
    const line = `블록${idx + 1}: ${repeatPrefix}${segText} (총 ${formatSec(totalSec)})`;

    return {
      index: idx,
      line,
      repeat,
      loopSec,
      totalSec,
      segs, // 필요하면 UI에서 더 쓰기 가능
    };
  });
};

const formatAmp = (a_uA) => {
  if (!Number.isFinite(a_uA)) return "-";
  // 1000uA = 1mA 라는 전제 (기존 mA(x)도 x*1000이라 동일)
  if (a_uA % 1000 === 0) return `${a_uA / 1000} mA`;
  return `${a_uA} uA`;
};

export const formatSecondsShort = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};

export const calcTotalSecondsFromRecipeJson = (recipeJson) => {
  if (!recipeJson || !Array.isArray(recipeJson.blocks)) return 0;

  let total = 0;
  for (const block of recipeJson.blocks) {
    const repeat = Number(block.repeat) || 1;
    const stims = Array.isArray(block.stims) ? block.stims : [];
    let loopTime = 0;

    for (const stim of stims) {
      const hold_s = Number(stim?.[3]) || 0;
      loopTime += Math.max(0, hold_s);
    }
    total += loopTime * Math.max(0, repeat);
  }
  return total;
};


  export const describeRecipeBlocks = (recipeJson, maxPatternsPerBlock = 8) => {
    if (!recipeJson || !Array.isArray(recipeJson.blocks)) return [];

    return recipeJson.blocks.map((block, bIdx) => {
      const repeat = Number(block?.repeat) || 1;
      const stims = Array.isArray(block?.stims) ? block.stims : [];

      // 1회 루프(블록 1회) 시간 = stim들의 hold 합
      let loopSec = 0;
      for (const stim of stims) {
        const h = Number(stim?.[3]) || 0;
        loopSec += Math.max(0, h);
      }
      const totalSec = loopSec * Math.max(0, repeat);

      // 블록 내부의 "반복하는 요소(패턴)" 요약: 같은 [w,d,a,h]를 묶어서 count
      const orderKeys = [];
      const acc = new Map(); // key -> {w,d,a,h,count,loopSec}
      for (const stim of stims) {
        const w = Number(stim?.[0]) || 0;
        const d = Number(stim?.[1]) || 0;
        const a = Number(stim?.[2]) || 0;
        const h = Number(stim?.[3]) || 0;

        const key = `${w}|${d}|${a}|${h}`;
        if (!acc.has(key)) {
          acc.set(key, { w, d, a, h, count: 0 });
          orderKeys.push(key);
        }
        acc.get(key).count += 1;
      }

      const patternLines = orderKeys
        .map((k) => acc.get(k))
        .slice(0, maxPatternsPerBlock)
        .map((p) => {
          const isRest = (p.w === 0 && p.a === 0) || (p.d === 1 && p.w === 0);
          const label = isRest ? "휴지" : "자극";

          // "루프에서 몇 번 반복되는지"까지 표현
          // 예: 자극: 500us / 200ms / 1mA / hold 30s × 1회/루프
          return `${label}: ${p.w}us / ${p.d}ms / ${formatAmp(p.a)} / hold ${p.h}s × ${p.count}회/루프`;
        });

      const title = `블록 ${bIdx + 1}`;
      const metaLine = `repeat ${repeat}회 · 루프 ${stims.length}개 step · 1회 루프 ${formatSecondsShort(loopSec)} · 블록 합계 ${formatSecondsShort(totalSec)}`;

      return {
        index: bIdx,
        title,
        repeat,
        stimCountPerLoop: stims.length,
        loopSec,
        totalSec,
        patternLines,
      };
    });
  };


export const summarizeRecipeJson = (recipeJson, totalDurationSecFromDB = null, maxPatterns = 4) => {
  if (!recipeJson || !Array.isArray(recipeJson.blocks)) {
    return {
      summaryLine: "레시피 형식이 올바르지 않습니다.",
      detailLines: [],
      totalSec: 0,
    };
  }

  const blockCount = recipeJson.blocks.length;
  let stimTotal = 0;

  const acc = new Map();

  for (const block of recipeJson.blocks) {
    const repeat = Number(block.repeat) || 1;
    const stims = Array.isArray(block.stims) ? block.stims : [];
    stimTotal += stims.length * repeat;

    for (const stim of stims) {
      const w = Number(stim?.[0]) || 0;
      const d = Number(stim?.[1]) || 0;
      const a = Number(stim?.[2]) || 0;
      const h = Number(stim?.[3]) || 0;

      const key = `${w}|${d}|${a}|${h}`;
      const sec = Math.max(0, h) * Math.max(0, repeat);

      const prev = acc.get(key) || { w, d, a, h, sec: 0 };
      prev.sec += sec;
      acc.set(key, prev);
    }
  }

  const totalSec =
    Number.isFinite(totalDurationSecFromDB) && totalDurationSecFromDB != null
      ? Number(totalDurationSecFromDB)
      : calcTotalSecondsFromRecipeJson(recipeJson);

  const patterns = Array.from(acc.values())
    .sort((x, y) => y.sec - x.sec)
    .slice(0, maxPatterns);

  const patternLines = patterns.map((p) => {
    const isRest = (p.w === 0 && p.a === 0) || (p.d === 1 && p.w === 0); // 대충 “휴지” 감지
    const label = isRest ? "휴지" : "자극";
    return `${label}: ${p.w}us / ${p.d}ms / ${formatAmp(p.a)} / hold ${p.h}s (≈${formatSecondsShort(p.sec)})`;
  });

  const summaryLine = `블록 ${blockCount}개 · 총 ${formatSecondsShort(totalSec)} · 자극 step(반복 포함) ${stimTotal}개`;

  return {
    summaryLine,
    detailLines: patternLines,
    totalSec,
  };
};

export function buildStimJsonFromBlocks(blocksSpec) {
    return {
      block_count: blocksSpec.length,
      blocks: blocksSpec.map((block) => ({
        repeat: block.repeat,
        stim_count: block.stims.length,
        stims: block.stims,
      })),
    };
  }

  export function makeBlocksFromLoop(loopStims, total) {
    const blocks = [];
  
    const loopTime = loopStims.reduce((sum, stim) => sum + stim[3], 0);
  
    if (loopTime <= 0) {
      return [
        {
          repeat: 1,
          stims: loopStims,
        },
      ];
    }
  
    const loops = Math.trunc(total / loopTime);
    const mod = total % loopTime;
  
    if (loops > 0) {
      blocks.push({
        repeat: loops,
        stims: loopStims,
      });
    }
  
    if (mod > 0) {
      const last = loopStims[loopStims.length - 1];
      const tailStim = [last[0], last[1], last[2], mod];
  
      blocks.push({
        repeat: 1,
        stims: [tailStim],
      });
    }
  
    return blocks;
  }
  
  
  export function buildCmdFromRecipe(recipe) {
    const parts = [];
  
    parts.push(920);
    parts.push(recipe.block_count);
  
    recipe.blocks.forEach((block) => {
      parts.push(block.stim_count);
      parts.push(block.repeat);
  
      block.stims.forEach((stim) => {
        // StimCommand = [width, duration, amp, hold]
        const [width, duration, amp, hold] = stim;
        parts.push(width, duration, amp, hold);
      });
    });
  
    return parts.join(" ");
  }
  
  // parsing total exepriment second
  export function calcTotalSecondsFromCmd(cmd) {
    if (!cmd || typeof cmd !== "string") return 0;
  
    const tokens = cmd
      .trim()
      .split(/[\s|,:()\[\]{}<>]+/)
      .filter(Boolean)
      .map((t) => Number(t));
  
    if (tokens.length < 3 || !Number.isFinite(tokens[0])) return 0;
  
    let i = 0;
    const opcode = tokens[i++];
    const blockCount = tokens[i++];

    let total = 0;
  
    for (let b = 0; b < blockCount && i < tokens.length; b++) {
      const stimCount = tokens[i++];
      const repeat = tokens[i++];
  
      let blockLoopTime = 0;
  
      for (let s = 0; s < stimCount && (i + 3) < tokens.length; s++) {
        const w_us = tokens[i++];
        const d_ms = tokens[i++];
        const a_uA = tokens[i++];
        const h_s  = tokens[i++];
  
        if (Number.isFinite(h_s)) {
          blockLoopTime += h_s;
        }
      }
      total += blockLoopTime * repeat;
    }
  
    return total;
  }


  export function buildTimelineFromCmd(cmd) {
    const segments = [];

    if (!cmd || typeof cmd !== "string") return segments;

    const tokens = cmd
      .trim()
      .split(/[\s|,:()\[\]{}<>]+/)
      .filter(Boolean)
      .map((t) => Number(t));

    if (tokens.length < 3 || !Number.isFinite(tokens[0])) return segments;

    let i = 0;
    const opcode = tokens[i++];
    const blockCount = tokens[i++];

    let currentStart = 0;

    for (let b = 0; b < blockCount && i < tokens.length; b++) {
      const stimCount = tokens[i++];
      const repeat = tokens[i++];

      const stimsInBlock = [];

      for (let s = 0; s < stimCount && (i + 3) < tokens.length; s++) {
        const w_us = tokens[i++];
        const d_ms = tokens[i++];
        const a_uA = tokens[i++];
        const h_s  = tokens[i++];

        stimsInBlock.push({ w_us, d_ms, a_uA, h_s });
      }

      for (let r = 0; r < repeat; r++) {
        for (const stim of stimsInBlock) {
          const h = Number.isFinite(stim.h_s) ? stim.h_s : 0;
          const start = currentStart;
          const end   = currentStart + Math.max(h, 0);

          segments.push({
            start,
            end,
            width: stim.w_us,
            duration: stim.d_ms,
            amp: stim.a_uA,
          });

          currentStart = end;
        }
      }
    }

    return segments;
  }


  
  /*

        Stimulation recipe

  */

    export const MIN_1  = 60;
    export const MIN_2  = 120;
    export const MIN_3  = 180;
    export const MIN_4  = 240;
    export const MIN_5  = 300;
    export const MIN_6  = 360;
    export const MIN_7  = 420;
    export const MIN_8  = 480;
    export const MIN_9  = 540;
    export const MIN_10 = 600;
    export const MIN_11 = 660;
    export const MIN_12 = 720;
    export const MIN_13 = 780;
    export const MIN_14 = 840;
    export const MIN_15 = 900;
    export const MIN_16 = 960;
    export const MIN_17 = 1020;
    export const MIN_18 = 1080;
    export const MIN_19 = 1140;
    export const MIN_20 = 1200;
    export const MIN_21 = 1260;
    export const MIN_22 = 1320;
    export const MIN_23 = 1380;
    export const MIN_24 = 1440;
    export const MIN_25 = 1500;
    export const MIN_26 = 1560;
    export const MIN_27 = 1620;
    export const MIN_28 = 1680;
    export const MIN_29 = 1740;
    export const MIN_30 = 1800;

    const mA = (x) => x * 1000;


    export function buildrecipe1() {
      const loop = [
          [250, 50, mA(3), MIN_10],
      ];

      const blocks = makeBlocksFromLoop(loop, MIN_10);
      const recipe = buildStimJsonFromBlocks(blocks);

      return buildCmdFromRecipe(recipe);
    }
    
  // 자극1-1
  export function buildrecipe1_1() {
    const loop = [
      [250, 25, mA(3), MIN_10],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }

  export function buildrecipe2() {
    const loop = [
      [250, 50,  mA(3), 15],
      [250, 50,  mA(4), 15],
      [250, 25,  mA(3), 15],
      [250, 25,  mA(4), 15],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);
    
    return buildCmdFromRecipe(recipe);
  }

// 자극3
export function buildrecipe3() {
    const loop = [
      [250, 50, mA(1), MIN_10],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극4
  export function buildrecipe4() {
    const loop = [
      [250, 50, mA(1), 15],
      [250, 50, mA(3), 15],
      [250, 25, mA(1), 15],
      [250, 25, mA(3), 15],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극M1
  export function buildrecipeM1() {
    const loop = [
      [50, 50, mA(1), MIN_10],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극M2
  export function buildrecipeM2() {
    const loop = [
      [500, 50, mA(0.4), MIN_10],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극M3
  export function buildrecipeM3() {
    const loop = [
      [50,  50, mA(1),   30],
      [500, 50, mA(0.4), 30],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극5
  export function buildrecipe5() {
    // Phase 1: 3Hz, 5분
    const loop1 = [
      [250, 333, mA(3), MIN_5],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_5);
  
    // Phase 2: 30Hz, 5분
    const loop2 = [
      [250, 33, mA(3), MIN_5],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_5);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극6
  export function buildrecipe6() {
    const loop = [
      [250, 333, mA(3), 15],
      [0,   1,   0,     15],
      [250, 50,  mA(4), 15],
      [0,   1,   0,     15],
      [250, 25,  mA(3), 30],
      [0,   1,   0,     30],
      [250, 50,  mA(4), 15],
    ];
  
    const blocks = makeBlocksFromLoop(loop, MIN_10);
    const recipe = buildStimJsonFromBlocks(blocks);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극7
  export function buildrecipe7() {
    // Phase 1: 5Hz, 8분
    const loop1 = [
      [400, 200, mA(1), 30],
      [0,   1,   0,     15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_8);
  
    // Phase 2: 10Hz, 7분
    const loop2 = [
      [250, 100, mA(3), 30],
      [0,   1,   0,     15],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_7);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);
    return buildCmdFromRecipe(recipe);
  }
  
  // 자극8
  export function buildrecipe8() {
    // Phase 1: 30Hz, 7분
    const loop1 = [
      [100, 33, mA(3), 30],
      [0,   1,  0,     15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_7);
  
    // Phase 2: 40Hz, 8분
    const loop2 = [
      [100, 25, mA(3), 30],
      [0,   1,  0,     15],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_8);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극9
  export function buildrecipe9() {
    // Phase 1
    const loop1 = [
      [500, 200, mA(1), 30],
      [0,   1,   0,     15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_7);
  
    // Phase 2: 10Hz 지속, 8분
    const loop2 = [
      [250, 100, mA(2), MIN_8],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_8);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극10
  export function buildrecipe10() {
    // Phase 1
    const loop1 = [
      [250, 50, mA(3), 30],
      [0,   1,  0,     15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_7);
  
    // Phase 2
    const loop2 = [
      [500, 50, mA(3), 30],
      [0,   1,  0,     15],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_8);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극11
  export function buildrecipe11() {
    // Phase 1
    const loop1 = [
      [500, 200, mA(1), 30],
      [0,   1,   0,     15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_7);
  
    // Phase 2
    const loop2 = [
      [200, 100, mA(1), MIN_8],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_8);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);

    return buildCmdFromRecipe(recipe);
  }
  
  // 자극12
  export function buildrecipe12() {
    // Phase 1
    const loop1 = [
      [250, 40, mA(1), 30],
      [0,   1,  0,     15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_7);
  
    // Phase 2
    const loop2 = [
      [500, 33, mA(1), 30],
      [0,   1,  0,     15],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_8);
  
    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2]);

    return buildCmdFromRecipe(recipe);
  }


export async function buildPersonal(defaultValue) {
  try {
    const res = await Api.getMyEffectiveRecipe(defaultValue);
    //console.log(res)
    const payload = res?.data ?? res;
    let recipeJson = payload?.recipe ?? payload;

    if (typeof recipeJson === "string") {
      recipeJson = JSON.parse(recipeJson);
    }

    return buildCmdFromRecipe(recipeJson);
  } catch (e) {
    console.error("[buildPersonal] failed to fetch recipe, fallback to hardcoded", e);

    // if error, starts default stimulation recipe
    const loop1 = [
      [500, 200, mA(1), 30],
      [0, 1, 0, 15],
    ];
    const blocks1 = makeBlocksFromLoop(loop1, MIN_7);

    const loop2 = [
      [200, 100, mA(1), MIN_8],
    ];
    const blocks2 = makeBlocksFromLoop(loop2, MIN_8);

    const loop3 = [
      [250, 50, mA(1), MIN_10],
    ];
    const blocks3 = makeBlocksFromLoop(loop3, MIN_10);

    const recipe = buildStimJsonFromBlocks([...blocks1, ...blocks2, ...blocks3]);
    return buildCmdFromRecipe(recipe);
  }
}